import { prop } from 'ramda';
import { useTranslations } from 'js/hooks/i18n';
import { useAppSelector } from 'js/store/hooks';
import { Icon } from 'js/components/common/Icon';
import { clsb, clsx } from 'js/utils/classNames.util';
import React, { useState, useCallback, memo } from 'react';
import { componentService } from 'js/services/component.service';

import * as Types from './types';
import { Label } from '../Label';
import * as helpers from '../../helpers';
import { withFinalFormField } from '../../hocs';
import { FieldBottomMessage } from '../FieldBottomMessage';

import '../../FormFields.scss';
import style from './Input.scss';

export const InputBare: React.FC<Types.IInputProps> = ({
    id,
    meta,
    hint,
    type,
    label,
    readOnly,
    disabled,
    leftIcon,
    rightIcon,
    className,
    classNames,
    infoTooltip,
    onMouseDown,
    currencySymbol,
    showPasswordEye,
    placeholder = '',
    isLabelAsteriskEnabled,
    isPlaceholderAsteriskEnabled,

    ...props
}) => {
    const [isPasswordVisible, setIsPasswordVisible] = useState(false);
    const translate = useTranslations('playtech.mobile.system.input.');
    const isEnablePasswordSpy = useAppSelector(prop('isEnablePasswordSpy'));
    const isDisabled = meta?.submitting || meta?.validating || disabled;
    const showPasswordIcon = (showPasswordEye || isEnablePasswordSpy) && type === 'password';

    const interactionClass = helpers.getInteractionClass({
        readOnly,
        error: meta?.error,
        disabled: isDisabled,
        active: meta?.active,
        touched: meta?.touched,
        submitting: meta?.submitting
    });

    const preventClick = useCallback((event: React.MouseEvent): void => event.preventDefault(), []);

    const togglePasswordVisible = useCallback(() => {
        setIsPasswordVisible(!isPasswordVisible);
    }, [setIsPasswordVisible, isPasswordVisible]);

    return (
        <div className={clsx([clsb([style, classNames], ['container']), interactionClass, className])}>
            {(label || isLabelAsteriskEnabled) && (
                <Label
                    id={id}
                    label={label}
                    classNames={classNames}
                    infoTooltip={infoTooltip}
                    isLabelAsteriskEnabled={isLabelAsteriskEnabled}
                />
            )}

            <div className={clsb([style, classNames], ['innerContainer'])}>
                {leftIcon && leftIcon()}

                {currencySymbol && <span className={clsb([style, classNames], ['currencySymbol'])}>{currencySymbol}</span>}

                <input
                    {...props}
                    id={id}
                    readOnly={readOnly}
                    disabled={isDisabled}
                    tabIndex={isDisabled || readOnly ? -1 : 0}
                    type={isPasswordVisible ? 'text' : type}
                    className={clsb([style, classNames], ['input'])}
                    onMouseDown={readOnly ? preventClick : onMouseDown}
                    placeholder={`${placeholder}${isPlaceholderAsteriskEnabled ? '*' : ''}`}
                    aria-label={props['aria-label'] || translate('playtech.mobile.system.input.aria-label')}
                />

                {showPasswordIcon &&
                    (isPasswordVisible ? (
                        <Icon
                            type="passwordEyeHide"
                            onClick={togglePasswordVisible}
                            disabled={isDisabled || readOnly}
                            className={clsb([style, classNames], ['showPasswordIcon'])}
                            aria-label={translate('password.aria-label.hide')}
                        />
                    ) : (
                        <Icon
                            type="passwordEyeShow"
                            onClick={togglePasswordVisible}
                            disabled={isDisabled || readOnly}
                            className={clsb([style, classNames], ['showPasswordIcon'])}
                            aria-label={translate('password.aria-label.show')}
                        />
                    ))}

                {rightIcon && rightIcon()}
            </div>

            <FieldBottomMessage hint={hint} meta={meta} classNames={classNames} />
        </div>
    );
};

export const InputPure = memo(InputBare);
export const Input = withFinalFormField<string>()(InputPure);

componentService.registerWebContentComponents({ Input: InputPure });
